import { useMemo, useState } from 'react';
import { api } from '@tvx/api-client';
import { useAsync } from '../lib/useAsync';
import { SkeletonRows } from '../components/Shimmer';
import { NewsThumbs } from './NewsThumbs';

type Item = {
  id: number; source: string; title: string; summary: string; url: string; symbol: string;
  at: string; pinned: boolean; hidden: boolean; image?: string | null;
};

const when = (at: string) => new Date(at).toLocaleString('en-IN', {
  day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata',
});

/**
 * The headlines the member area and the apps show under Market News. They come
 * from the publishers' open feeds on their own; this is where the desk can pin
 * one to the top, hide one, or tag it with a stock.
 */
export function MarketNewsAdmin() {
  const { data, error, loading, reload } = useAsync((signal) =>
    api.get<{ items: Item[]; feeds: string[] }>('/market-news', signal));
  const [source, setSource] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');

  const rows = useMemo(() => (data?.items ?? []).filter((i) => !source || i.source === source), [data, source]);

  async function patch(id: number, body: Partial<Pick<Item, 'hidden' | 'pinned' | 'symbol'>>) {
    setNote('');
    try { await api.put(`/market-news/${id}`, body); await reload(); } catch (e) { setNote((e as Error).message); }
  }

  async function refresh() {
    setBusy(true); setNote('');
    try {
      const r = await api.post<{ report: { source: string; added?: number; error?: string }[] }>('/market-news/refresh', {});
      setNote(r.report.map((x) => `${x.source}: ${x.error ? `failed (${x.error})` : `${x.added} new`}`).join(' · '));
      await reload();
    } catch (e) { setNote((e as Error).message); }
    setBusy(false);
  }

  return (
    <div className="pg">
      <div className="rowbar">
        <div>
          <h1 style={{ fontSize: 29 }}>Market News</h1>
          <p style={{ fontSize: 13.5, color: 'var(--tx2)', marginTop: 7, maxWidth: 680, lineHeight: 1.55 }}>
            Headlines from {data?.feeds.join(', ') || 'the publishers'} arrive by themselves every 15 minutes and show in
            News &amp; Analysis. Each opens the publisher's page. Pin one to keep it at the top, or hide one members should not see.
          </p>
        </div>
        <button className="b bg" type="button" disabled={busy} onClick={() => void refresh()}>
          {busy ? 'Fetching…' : 'Fetch now'}
        </button>
      </div>

      {error ? <div className="err">{String((error as Error).message)}</div> : null}
      {note ? <div className="crd" style={{ padding: '10px 16px', marginBottom: 12, fontSize: 12.5 }}>{note}</div> : null}

      <NewsThumbs onChanged={() => void reload()} />

      <div className="crd" style={{ padding: '20px 22px' }}>
        <div className="sg" style={{ marginBottom: 16 }}>
          {['', ...(data?.feeds ?? [])].map((f) => (
            <span key={f} {...(source === f ? { 'data-on': '' } : {})} onClick={() => setSource(f)}>{f || 'All sources'}</span>
          ))}
        </div>
        <div className="tblx">
          <table className="tbl">
            <thead><tr><th>Headline</th><th>Source</th><th>Posted</th><th>Stock</th><th /></tr></thead>
            <tbody>
              {loading && !data ? <SkeletonRows cols={5} /> : rows.length === 0 ? (
                <tr><td colSpan={5} className="muted" style={{ padding: '26px 8px' }}>
                  No headlines yet. Press Fetch now, or wait a few minutes.
                </td></tr>
              ) : rows.map((i) => (
                <tr key={i.id} style={i.hidden ? { opacity: 0.5 } : undefined}>
                  <td style={{ maxWidth: 520 }}>
                    {i.image ? <img src={i.image} alt="" width={64} height={36}
                      style={{ borderRadius: 6, objectFit: 'cover', float: 'left', marginRight: 10 }} /> : null}
                    <a href={i.url} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600, color: 'inherit', textDecoration: 'none' }}>{i.title}</a>
                    {i.pinned ? <span className="tg" style={{ marginLeft: 8 }}>pinned</span> : null}
                    {i.hidden ? <span className="tg" style={{ marginLeft: 8 }}>hidden</span> : null}
                  </td>
                  <td className="muted">{i.source}</td>
                  <td className="muted">{when(i.at)}</td>
                  <td>
                    <input className="inp" style={{ width: 110 }} defaultValue={i.symbol} placeholder="e.g. TCS"
                      onBlur={(e) => { const v = e.target.value.trim().toUpperCase(); if (v !== i.symbol) void patch(i.id, { symbol: v }); }} />
                  </td>
                  <td className="right" style={{ whiteSpace: 'nowrap' }}>
                    <button className="b bo" style={{ padding: '6px 12px', fontSize: 11.5 }} type="button"
                      onClick={() => void patch(i.id, { pinned: !i.pinned })}>{i.pinned ? 'Unpin' : 'Pin'}</button>{' '}
                    <button className="b bo" style={{ padding: '6px 12px', fontSize: 11.5 }} type="button"
                      onClick={() => void patch(i.id, { hidden: !i.hidden })}>{i.hidden ? 'Show' : 'Hide'}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
